/* eslint-disable no-use-before-define */
import React, { useEffect, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Drawer,
  SwipeableDrawer,
  Hidden,
  Avatar,
  Link,
  List,
  ListSubheader,
  Typography,
  makeStyles
} from '@material-ui/core';
import useAuth from 'src/hooks/useAuth';
import { useSelector } from 'src/store';
import Logo from 'src/components/Logo/LogoBlue';
import NavItem from './NavItem';
import { customerSides, adminSides } from './Sidebars';

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles((theme) => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  },
  subheader: {
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,

    '& .MuiListSubheader-root': {
      fontWeight: 'bold !important'
    }
  }
}));

const NavBar = ({ onMobileClose, openMobile, onOpenMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { openTour, scrollToBottom } = useSelector((state) => state.toggle);
  const { user } = useAuth();
  const ps = useRef();

  if (openTour && !scrollToBottom) {
    const curr = ps.current;
    if (curr) {
      curr.scrollTop = 0;
    }
  }

  if (openTour && scrollToBottom) {
    const curr = ps.current;
    if (curr) {
      curr.scrollTop = 100;
    }
  }

  // switch between customer and admin sidebars
  //   const section = ()=> {
  //     switch(user){
  // case 'customer':
  //   return customerSides;
  // case 'admin':
  //       return adminSides
  //     }
  //   }

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <Box>
        <Hidden lgUp>
          <Box p={2}>
            <Box display="flex" justifyContent="center">
              <RouterLink to="/app/account">
                <Avatar
                  alt="User"
                  className={classes.avatar}
                  src={user.userProfilePicture}
                />
              </RouterLink>
            </Box>
            <Box mt={2} textAlign="center">
              <Link
                component={RouterLink}
                to="/app/account"
                variant="h5"
                color="textPrimary"
                underline="none"
              >
                {user.userFirstName} {user.userLastName}
              </Link>
            </Box>
          </Box>
        </Hidden>
        <Divider />
      </Box>
      <PerfectScrollbar
        options={{ suppressScrollX: true }}
        containerRef={(el) => (ps.current = el)}
      >
        <Box p={2} flexGrow={1}>
          {customerSides.map((section) => (
            <List
              key={section.subheader}
              className={classes.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <SwipeableDrawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          onOpen={onOpenMobile}
        >
          {content}
        </SwipeableDrawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  onOpenMobile: PropTypes.func
};

export default NavBar;
